import React, { useState, useEffect } from 'react';
import { Clock, Eye } from 'react-feather';
import type { VersionHistory } from '../types';
import { getVersionHistory } from '../api';

export const History: React.FC = () => {
  const [publishHistory, setPublishHistory] = useState<VersionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const history = await getVersionHistory();
      setPublishHistory(history);
    } catch (err) {
      console.error('Failed to load publish history:', err);
      setError('Failed to load publish history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const handleViewDetails = (itemId: string) => {
    setSelectedItem(itemId === selectedItem ? null : itemId);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="header">
          <h2>Publish History</h2>
          <p>Track when buckets were created and updated</p>
        </div>
        <div className="card">
          <div className="empty-state">
            <p>Loading publish history...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="header">
          <h2>Publish History</h2>
          <p>Track when buckets were created and updated</p>
        </div>
        <div className="card" style={{ background: '#fff5f5', borderColor: '#feb2b2' }}>
          <div style={{ color: '#991b1b', padding: '20px', textAlign: 'center' }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h2>Publish History</h2>
        <p>Track when buckets were created and updated</p>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div className="card" style={{ textAlign: 'center', padding: '30px 20px' }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '5px', color: '#2383E2' }}>
            {publishHistory.length}
          </div>
          <div style={{ color: '#666', fontSize: '14px' }}>Total Publishes</div>
        </div>

        <div className="card" style={{ textAlign: 'center', padding: '30px 20px' }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '5px', color: '#8b5cf6' }}>
            {publishHistory[0]?.templateCount || 0}
          </div>
          <div style={{ color: '#666', fontSize: '14px' }}>Latest Media Count</div>
        </div>
      </div>

      {/* Publish Timeline */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>Publish Timeline</h3>
        </div>

        <div style={{ position: 'relative', paddingLeft: '40px' }}>
          {/* Timeline line */}
          <div style={{
            position: 'absolute',
            left: '15px',
            top: '20px',
            bottom: '20px',
            width: '2px',
            background: 'rgba(55, 53, 47, 0.1)'
          }} />

          {publishHistory.map((item, index) => (
            <div
              key={item.id}
              style={{
                position: 'relative',
                marginBottom: index < publishHistory.length - 1 ? '32px' : '0',
                paddingBottom: '20px'
              }}
            >
              {/* Timeline dot */}
              <div style={{
                position: 'absolute',
                left: '-33px',
                top: '8px',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                background: index === 0 ? '#2383E2' : '#e0e0e0',
                border: '3px solid white',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
              }} />

              <div style={{
                border: '1px solid rgba(55, 53, 47, 0.09)',
                borderRadius: '6px',
                padding: '16px',
                background: index === 0 ? 'rgba(35, 131, 226, 0.02)' : '#fff'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px' }}>
                      {item.packTitle}
                      {index === 0 && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '11px',
                          padding: '2px 8px',
                          background: '#2383E2',
                          color: 'white',
                          borderRadius: '3px'
                        }}>
                          LATEST
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={13} />
                        {new Date(item.publishedAt).toLocaleDateString()} at{' '}
                        {new Date(item.publishedAt).toLocaleTimeString()}
                      </span>
                      <span>•</span>
                      <span>{item.publishedBy}</span>
                      <span>•</span>
                      <span>{item.templateCount} media items</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleViewDetails(item.id)}
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      <Eye size={13} />
                      {selectedItem === item.id ? 'Hide' : 'View'}
                    </button>
                  </div>
                </div>

                {/* Changes */}
                {item.changes && item.changes.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#666', marginBottom: '6px' }}>
                      Changes:
                    </div>
                    <ul style={{ fontSize: '13px', color: '#666', marginLeft: '20px', lineHeight: '1.6' }}>
                      {item.changes.map((change, idx) => (
                        <li key={idx}>{change}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Expanded Details */}
                {selectedItem === item.id && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: 'rgba(55, 53, 47, 0.03)',
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#666'
                  }}>
                    <p><strong>Bucket ID:</strong> {item.packId}</p>
                    <p style={{ marginTop: '4px' }}><strong>Published By:</strong> {item.publishedBy}</p>
                    <p style={{ marginTop: '4px' }}><strong>Media Count:</strong> {item.templateCount}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="card" style={{ marginTop: '20px' }}>
        <h3 style={{ marginBottom: '12px' }}>About Publish History</h3>
        <ul style={{ fontSize: '13px', color: '#666', marginLeft: '20px', lineHeight: '1.8' }}>
          <li>Track when buckets were created and updated</li>
          <li>See who published changes and when</li>
          <li>View media count for each publish</li>
          <li>Publish history is preserved for audit purposes</li>
        </ul>
      </div>
    </div>
  );
};
